import {
  DATA,
  ABILITY_SLOTS,
  CLASS_IDS,
  type AbilityDef,
  type AbilitySlot,
  type TalentEffect,
  type TalentNode,
} from '../types.js';
import { itemDef } from './shop.js';

/**
 * feature 4.3 — talent trees (system + placeholder trees).
 *
 * One passive-modifier tree per base ability (`DATA.talents`, keyed by ability
 * id). A node costs 1 skill point (the unified pool) and does nothing until its
 * parent ability is activated. `effectiveAbility` returns an ability def with
 * every owned node for that ability applied; the cast handlers read the
 * ability's numbers through it. Pure, deterministic, no RNG.
 */

interface NodeLoc {
  node: TalentNode;
  /** The ability id this node's tree belongs to. */
  abilityId: string;
  /** The ability slot (`q`/`w`/`e`/`r`) the ability occupies in its class book. */
  slot: AbilitySlot;
}

/** ability id → the slot it occupies (ability ids are globally unique). */
const ABILITY_SLOT = new Map<string, AbilitySlot>();
for (const cls of CLASS_IDS) {
  const book = DATA.abilities[cls];
  for (const slot of ABILITY_SLOTS) ABILITY_SLOT.set(book[slot].id, slot);
}

/** node id → its location (node, parent ability id, slot). Built once. */
const NODE_LOC = new Map<string, NodeLoc>();
for (const [abilityId, nodes] of Object.entries(DATA.talents)) {
  if (abilityId === '_comment' || !Array.isArray(nodes)) continue;
  const slot = ABILITY_SLOT.get(abilityId);
  if (!slot) continue;
  for (const node of nodes) NODE_LOC.set(node.id, { node, abilityId, slot });
}

/** feature 4.3 — every talent node id, across every ability tree. */
export const TALENT_NODE_IDS: string[] = [...NODE_LOC.keys()];

/** The ordered node list for `abilityId` (empty for an unknown id). */
export function talentTree(abilityId: string): TalentNode[] {
  const nodes = DATA.talents[abilityId];
  return Array.isArray(nodes) ? nodes : [];
}

/** Resolve a node by id, or `undefined`. */
export function talentNode(nodeId: string): TalentNode | undefined {
  return NODE_LOC.get(nodeId)?.node;
}

/** The ability id whose tree `nodeId` belongs to, or `undefined`. */
export function nodeAbilityId(nodeId: string): string | undefined {
  return NODE_LOC.get(nodeId)?.abilityId;
}

/** The ability slot `nodeId`'s parent ability occupies, or `undefined`. */
export function nodeSlot(nodeId: string): AbilitySlot | undefined {
  return NODE_LOC.get(nodeId)?.slot;
}

/** The slot an ability id occupies in its class book, or `undefined`. */
export function abilitySlotOf(abilityId: string): AbilitySlot | undefined {
  return ABILITY_SLOT.get(abilityId);
}

/** The minimal player shape `effectiveAbility` needs (avoids a `world.ts` cycle). */
export interface TalentedPlayer {
  /** Sorted list of owned talent node ids. */
  talentNodes: readonly string[];
  /** Points invested per ability slot (0 = un-activated). */
  abilityPoints: { q: number; w: number; e: number; r: number };
  /**
   * feature 4.5 — held item catalog ids (`null` / `''` = empty slot). A held
   * `+1 <slot>` item (`ItemDef.skillBonus`) activates that slot even with no
   * point spent. Optional — a caller that predates 4.5 omits it and is treated
   * as holding nothing, so every gate stays byte-identical to 4.4.
   */
  inventory?: readonly (string | null)[];
}

/**
 * feature 4.5 — the minimal player shape the effective-ability-point helpers
 * need: the points actually SPENT per slot plus the held-item inventory.
 */
export interface SkillPointedPlayer {
  abilityPoints: { q: number; w: number; e: number; r: number };
  /** Held item catalog ids; `null` / `''` / unknown ids are ignored. */
  inventory?: readonly (string | null)[];
}

/**
 * feature 4.5 (criterion 4) — the player's EFFECTIVE points in `slot`: the
 * points actually spent (`abilityPoints[slot]`) plus the summed
 * `skillBonus.amount` of every held item whose `skillBonus.slot === slot`. The
 * inventory is walked in slot order and catalog lookups are by id — pure,
 * deterministic, no RNG. With no `+1` item held this is exactly
 * `abilityPoints[slot]`, so every gate that reads it is byte-identical to 4.4
 * (criterion 11).
 */
export function effectiveAbilityPoints(player: SkillPointedPlayer, slot: AbilitySlot): number {
  let n = player.abilityPoints[slot] ?? 0;
  const inv = player.inventory;
  if (inv) {
    for (const id of inv) {
      if (!id) continue;
      const bonus = itemDef(id)?.skillBonus;
      if (bonus && bonus.slot === slot) n += bonus.amount;
    }
  }
  return n;
}

/**
 * feature 4.5 (criterion 5) — is ability `slot` activated for `player`? True
 * once the effective point total (spent points + held `+1` items) reaches 1.
 * Gates casting (`World.resolveCast`) and the talent "parent activated" check
 * (`World.spendTalentPoint`, `effectiveAbility`).
 */
export function abilityActivated(player: SkillPointedPlayer, slot: AbilitySlot): boolean {
  return effectiveAbilityPoints(player, slot) >= 1;
}

function applyTalentEffect(copy: Record<string, unknown>, effect: TalentEffect): void {
  const { target, mode, value } = effect;
  if (target === 'damagePct') {
    // Synthetic target — a final-damage multiplier. Folding it into BOTH
    // `damage` and `scale` is exactly equivalent to multiplying the
    // `abilityDamage` result `(damage + attr*scale)` by `(1 + value)`.
    const m = 1 + value;
    if (typeof copy.damage === 'number') copy.damage *= m;
    if (typeof copy.scale === 'number') copy.scale *= m;
    return;
  }
  const cur = copy[target];
  if (typeof cur !== 'number') return; // unknown / non-numeric target — skip
  copy[target] = mode === 'addFlat' ? cur + value : cur * (1 + value);
}

/**
 * feature 4.3 (criterion 8) — the ability def `def` with every talent node the
 * player owns for THAT ability applied. Defensive: a node whose parent ability
 * is not activated (`abilityActivated` — no spent point AND no `+1` item,
 * feature 4.5) is skipped (criterion 10). When
 * the player owns no applicable node the RAW def is returned unchanged (a
 * zero-alloc fast path — guarantees zero-node behaviour is byte-identical to
 * pre-4.3, criterion 11); otherwise a shallow copy is modified and returned, so
 * the shared `DATA` def is never mutated. Pure, no RNG; node application order
 * is the player's (sorted) `talentNodes` order and is irrelevant for the
 * current 1-node-per-target set.
 */
export function effectiveAbility(player: TalentedPlayer, def: AbilityDef): AbilityDef {
  let copy: AbilityDef | null = null;
  for (const nodeId of player.talentNodes) {
    const loc = NODE_LOC.get(nodeId);
    if (!loc || loc.abilityId !== def.id) continue;
    if (!abilityActivated(player, loc.slot)) continue; // parent not activated (spent point or +1 item)
    if (!copy) copy = { ...def };
    applyTalentEffect(copy as unknown as Record<string, unknown>, loc.node.effect);
  }
  return copy ?? def;
}

/** feature 4.3 — how many owned nodes `player` has for `abilityId`. */
export function ownedNodeCount(player: TalentedPlayer, abilityId: string): number {
  let n = 0;
  for (const nodeId of player.talentNodes) {
    if (NODE_LOC.get(nodeId)?.abilityId === abilityId) n++;
  }
  return n;
}
